export type RunSegmentWithHr = {
  index: number;
  durationSeconds: number;
  distanceMeters: number;
  avgHeartRateBpm?: number;
  time?: string;
};

/** Fill missing per-segment avg HR from cumulative elapsed windows + HR samples. */
export function enrichSegmentsWithAvgHr(
  segments: RunSegmentWithHr[],
  startTimeMs: number,
  hrSamples: Array<{ timestamp: number; bpm: number }>,
): RunSegmentWithHr[] {
  if (!segments.length || !hrSamples.length || startTimeMs <= 0) return segments;

  let elapsedOffset = 0;
  return segments.map((seg) => {
    const existing = seg.avgHeartRateBpm;
    if (existing != null && existing > 0) {
      elapsedOffset += seg.durationSeconds;
      return seg;
    }

    const startMs = startTimeMs + elapsedOffset * 1000;
    const endMs = startTimeMs + (elapsedOffset + seg.durationSeconds) * 1000;
    elapsedOffset += seg.durationSeconds;

    const window = hrSamples.filter(
      (s) => s.timestamp >= startMs - 800 && s.timestamp <= endMs + 800,
    );
    if (window.length === 0) return seg;

    const avgHeartRateBpm = Math.round(
      window.reduce((sum, s) => sum + s.bpm, 0) / window.length,
    );
    return avgHeartRateBpm > 0 ? { ...seg, avgHeartRateBpm } : seg;
  });
}

const segmentIndex = (value: unknown): number => {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
};

export function mergeWatchSegmentsIntoPhone(
  phone: RunSegmentWithHr[],
  watch: RunSegmentWithHr[],
  options?: { appendNew?: boolean },
): RunSegmentWithHr[] {
  const appendNew = options?.appendNew !== false;
  if (!watch.length) return phone;
  if (!phone.length) return appendNew ? watch : phone;

  const watchByIndex = new Map(watch.map((s) => [segmentIndex(s.index), s]));
  const merged = phone.map((p) => {
    const pIndex = segmentIndex(p.index);
    const w = watchByIndex.get(pIndex);
    if (!w) return { ...p, index: pIndex };
    return {
      ...p,
      index: pIndex,
      durationSeconds: w.durationSeconds > 0 ? w.durationSeconds : p.durationSeconds,
      distanceMeters: w.distanceMeters > 0 ? w.distanceMeters : p.distanceMeters,
      avgHeartRateBpm:
        (p.avgHeartRateBpm != null && p.avgHeartRateBpm > 0)
          ? p.avgHeartRateBpm
          : (w.avgHeartRateBpm != null && w.avgHeartRateBpm > 0)
            ? w.avgHeartRateBpm
            : p.avgHeartRateBpm,
    };
  });

  if (appendNew) {
    for (const w of watch) {
      const wIndex = segmentIndex(w.index);
      if (!merged.some((m) => segmentIndex(m.index) === wIndex)) {
        merged.push({ ...w, index: wIndex });
      }
    }
  }
  return merged.sort((a, b) => segmentIndex(a.index) - segmentIndex(b.index));
}

/** Sum elapsed/distance already recorded in marked segments (syncs phone window after watch marks). */
export function segmentWindowFromRecorded(segments: RunSegmentWithHr[]): {
  elapsedSeconds: number;
  distanceMeters: number;
} {
  return {
    elapsedSeconds: segments.reduce((sum, seg) => sum + seg.durationSeconds, 0),
    distanceMeters: segments.reduce((sum, seg) => sum + seg.distanceMeters, 0),
  };
}

/** True when marked segments already account for the full run (watch may have auto-closed trailing lap). */
export function segmentsCoverFullRun(
  segments: RunSegmentWithHr[],
  totalDistanceM: number,
  totalElapsedSec: number,
): boolean {
  if (segments.length === 0) return false;
  const distSum = segments.reduce((sum, seg) => sum + seg.distanceMeters, 0);
  const elapsedSum = segments.reduce((sum, seg) => sum + seg.durationSeconds, 0);
  return distSum >= totalDistanceM - 20 && elapsedSum >= totalElapsedSec - 8;
}
